<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFixtureStore } from '../../stores/fixtureStore'
import DateRangePicker from '../../components/DateRangePicker.vue'
import FixtureCalendarModal from '../../components/FixtureCalendarModal.vue'
import CreateFixtureForm from '../../components/CreateFixtureForm.vue'
import ConfirmationDialog from '../../components/ConfirmationDialog.vue'
import DateTimePickerCustom from '../../components/DateTimePickerCustom.vue'
import axios from '../../axios'
import { useCurrentUserPermissions } from '../../composables/usePermissions'
import { useProjectionsStore } from '../../stores/projections'
import { useUserStore } from '../../stores/userStore'

const { hasAnyPermission } = useCurrentUserPermissions()
const props = defineProps({
  currentLeagueName: {
    type: String,
    default: ''
  },
  filters: {
    type: Object,
    required: true
  }
})

const fixtureStore = useFixtureStore()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const searchQuery = ref('')
const fixtures = computed(() => fixtureStore.filteredFixtures)

const formatTime = (timeString) => {
  return timeString.substring(0, 5)
}

const handleFixtureClick = async (fixtureId) => {
  router.push(`/fixture/${fixtureId}`)
}

const sortOrder = ref('asc')

const groupedFixtures = computed(() => {
  const groups = fixtures.value.reduce((groups, fixture) => {
    const date = fixture.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(fixture);
    return groups;
  }, {});

  const sortedDates = Object.keys(groups).sort((a, b) => {
    const dateA = new Date(a)
    const dateB = new Date(b)
    return sortOrder.value === 'asc' ? dateA - dateB : dateB - dateA
  });

  const sortedGroups = {}
  sortedDates.forEach(date => {
    sortedGroups[date] = groups[date]
  })

  return sortedGroups
})

const groupedRecentFixtures = computed(() => {
  const groups = recentFixtures.value.reduce((groups, fixture) => {
    // Extract date from gamedate field (format: "YYYY-MM-DD HH:MM:SS")
    const date = fixture.gamedate ? fixture.gamedate.split(' ')[0] : 'Unknown';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(fixture);
    return groups;
  }, {});

  const sortedDates = Object.keys(groups).sort((a, b) => {
    const dateA = new Date(a)
    const dateB = new Date(b)
    return sortOrder.value === 'asc' ? dateA - dateB : dateB - dateA
  });

  const sortedGroups = {}
  sortedDates.forEach(date => {
    sortedGroups[date] = groups[date]
  })

  return sortedGroups
})

const fixturesRange = ref('next3')
const customRange = ref({
  start: '',
  end: ''
})

watch(fixturesRange, (newRange) => {
  if (newRange !== 'custom') {
    const today = new Date()
    let startDate = new Date()
    let endDate = new Date()

    switch(newRange) {
      case 'prev7':
      case 'prev14':
      case 'prev30':
      case 'prev90':
        const days = parseInt(newRange.replace('prev', ''))
        startDate.setDate(today.getDate() - days)
        endDate = today
        break
      case 'next7':
      case 'next14':
      case 'next30':
        const nextDays = parseInt(newRange.replace('next', ''))
        startDate = today
        endDate.setDate(today.getDate() + nextDays)
        break
      case 'today':
        startDate = today
        endDate = today
        break
      case 'tomorrow':
        startDate.setDate(today.getDate() + 1)
        endDate.setDate(today.getDate() + 1)
        break
      case 'next3':
        startDate = today
        endDate.setDate(today.getDate() + 3)
        break
    }

    // Update customRange so the calendar shows the selected range
    customRange.value = {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    }

    fixtureStore.fetchFixturesByCustomRange(
      startDate.toISOString().split('T')[0],
      endDate.toISOString().split('T')[0]
    )

    // Also fetch recent fixtures with the new date range
    fetchRecentFixtures()
  }
})

watch(customRange, (newRange) => {
  if (fixturesRange.value === 'custom' && newRange.start && newRange.end) {
    fixtureStore.fetchFixturesByCustomRange(newRange.start, newRange.end)
    
    // Also fetch recent fixtures with the custom date range
    fetchRecentFixtures()
  }
}, { deep: true })

const showRangeDropdown = ref(false)
const showFixtureCalendar = ref(false)
const fixtureDates = ref([])

const fetchFixtureDates = async () => {
  const leagueId = props.filters.league_id
  if (!leagueId) return
  try {
    const { data } = await axios.get(`/api/v2/fixture-dates-by-league/${leagueId}`)
    fixtureDates.value = data.map(item => item.date)
  } catch (err) {
    console.error('Failed to fetch fixture dates:', err)
  }
}

watch(showRangeDropdown, (isOpen) => {
  if (isOpen) {
    fetchFixtureDates()
  }
})

const rangeOptions = [
  { value: 'prev7', label: 'Last 7 days' },
  { value: 'prev14', label: 'Last 14 days' },
  { value: 'prev30', label: 'Last 30 days' },
  { value: 'prev90', label: 'Last 3 months' },
  { value: 'next7', label: 'Next 7 days' },
  { value: 'next14', label: 'Next 14 days' },
  { value: 'next30', label: 'Next 30 days' },
  { value: 'custom', label: 'Custom Range' }
]

const quickRanges = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'next3', label: 'Next 3' },
  { value: 'next7', label: 'Next 7' },
]

const getRangeText = computed(() => {
  if (fixturesRange.value === 'custom' && customRange.value.start && customRange.value.end) {
    const startDate = new Date(customRange.value.start)
    const endDate = new Date(customRange.value.end)
    return `${formatDateEst(startDate)} - ${formatDateEst(endDate)}`
  }
  return rangeOptions.find(opt => opt.value === fixturesRange.value)?.label || 'Select Range'
})

const formatDateEst = (date) => {
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const selectRange = (value) => {
  fixturesRange.value = value
  if (value !== 'custom') {
    showRangeDropdown.value = false
  }
}

const applyCustomRange = () => {
  if (customRange.value.start && customRange.value.end) {
    fixtureStore.fetchFixturesByCustomRange(customRange.value.start, customRange.value.end)
    
    // Also fetch recent fixtures with the custom date range
    fetchRecentFixtures()
    
    showRangeDropdown.value = false
  }
}

const handleClickOutside = (e) => {
  const dropdown = e.target.closest('.range-dropdown')
  const isClickInside = dropdown !== null
  if (!isClickInside) {
    showRangeDropdown.value = false
  }
}

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  
  const today = new Date()
  const endDate = new Date()
  endDate.setDate(today.getDate() + 3)

  fixtureStore.fetchFixturesByCustomRange(
    today.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0]
  )

  fetchRecentFixtures()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
}

const recentFixtures = ref([])
const isLoadingRecent = ref(false)
const recentError = ref(null)

// Delete confirmation state
const showDeleteConfirmation = ref(false)
const fixtureToDelete = ref(null)
const isDeleting = ref(false)

const fetchRecentFixtures = async () => {
  isLoadingRecent.value = true
  recentError.value = null
  try {
    const leagueId = props.filters.league_id
    console.log('Fetching recent fixtures for league:', leagueId)
    
    if (!leagueId) {
      console.warn('No league ID provided, skipping fetch')
      recentFixtures.value = []
      return
    }

    // Calculate date range based on current filter
    const today = new Date()
    let startDate = new Date()
    let endDate = new Date()

    switch(fixturesRange.value) {
      case 'prev7':
      case 'prev14':
      case 'prev30':
      case 'prev90':
        const days = parseInt(fixturesRange.value.replace('prev', ''))
        startDate.setDate(today.getDate() - days)
        endDate = today
        break
      case 'next7':
      case 'next14':
      case 'next30':
        const nextDays = parseInt(fixturesRange.value.replace('next', ''))
        startDate = today
        endDate.setDate(today.getDate() + nextDays)
        break
      case 'today':
        endDate = today
        break
      case 'tomorrow':
        startDate.setDate(today.getDate() + 1)
        endDate.setDate(today.getDate() + 1)
        break
      case 'next3':
        endDate.setDate(today.getDate() + 3)
        break
      case 'custom':
        if (customRange.value.start && customRange.value.end) {
          startDate = new Date(customRange.value.start)
          endDate = new Date(customRange.value.end)
        }
        break
      default:
        // Default to next 3 days
        endDate.setDate(today.getDate() + 3)
    }

    // Format dates for API
    const formattedStartDate = startDate.toISOString().split('T')[0]
    const formattedEndDate = endDate.toISOString().split('T')[0]

    // Build API URL with date parameters
    let apiUrl = `/api/v2/scheduled-games-by-league/${leagueId}`
    const params = new URLSearchParams({
      date_from: formattedStartDate,
      date_to: formattedEndDate
    })
    apiUrl += `?${params.toString()}`

    // console.log('Fetching recent fixtures with date range:', formattedStartDate, 'to', formattedEndDate)

    const { data } = await axios.get(apiUrl)

    // console.log("recent fixtures filtered by league and date", leagueId, ":", data)
    
    recentFixtures.value = Array.isArray(data) ? data : []
  } catch (err) {
    recentError.value = err.response?.data?.message || 'Failed to fetch recent fixtures.'
    recentFixtures.value = []
  } finally {
    isLoadingRecent.value = false
  }
}

watch(() => props.filters, fetchRecentFixtures, { deep: true, immediate: true })

// Handle newly created fixture
const handleNewFixture = (newFixture) => {
  // console.log('New fixture created:', newFixture)
  
  const currentLeagueId = props.filters.league_id
  if (newFixture.league_id?.toString() === currentLeagueId?.toString()) {
    recentFixtures.value.unshift(newFixture)
    // console.log('Added new fixture to recent fixtures list')
  } else {
    // console.log('New fixture belongs to different league, not adding to current list')
  }
}

// Handle delete fixture
const handleDeleteFixture = (fixture) => {
  // console.log('Attempting to delete fixture:', fixture)
  fixtureToDelete.value = fixture
  showDeleteConfirmation.value = true
}

const confirmDeleteFixture = async () => {
  if (!fixtureToDelete.value) return
  
  isDeleting.value = true
  try {
    const fixtureId = fixtureToDelete.value.id
    console.log('Deleting fixture with ID:', fixtureId)
    
    await axios.delete(`/api/fixtures/${fixtureId}`)
    
    const index = recentFixtures.value.findIndex(f => f.id === fixtureId)
    if (index !== -1) {
      recentFixtures.value.splice(index, 1)
      console.log('Fixture deleted successfully')
    }
    
    showDeleteConfirmation.value = false
    fixtureToDelete.value = null
  } catch (err) {
    console.error('Error deleting fixture:', err)
  } finally {
    isDeleting.value = false
  }
}

const cancelDeleteFixture = () => {
  showDeleteConfirmation.value = false
  fixtureToDelete.value = null
}

// Bulk download state
const selectedFixtureIds = ref(new Set())
const showBulkDownloadPanel = ref(false)
const isBulkDownloading = ref(false)
const bulkDownloadType = ref('balanced_lines') // 'milestones' or 'balanced_lines'
const defaultBulkMarkets = ['points', 'rebounds', 'three_point_field_goal', 'assists']
const selectedBulkMarkets = ref([...defaultBulkMarkets])
const availableBulkMarkets = [
  { value: 'points', label: 'Points' },
  { value: 'rebounds', label: 'Rebounds' },
  { value: 'three_point_field_goal', label: '3PT FG' },
  { value: 'assists', label: 'Assists' },
  { value: 'steals', label: 'Steals' },
  { value: 'blocks', label: 'Blocks' },
  { value: 'turnovers', label: 'Turnovers' },
  { value: 'pra', label: 'PRA' },
  { value: 'pr', label: 'PR' },
  { value: 'pa', label: 'PA' },
  { value: 'ra', label: 'RA' },
]

const canBulkDownload = computed(() => !!userStore.currentUser?.id)
const hasSelectedFixtures = computed(() => selectedFixtureIds.value.size > 0)

const toggleFixtureSelection = (id) => {
  const newSet = new Set(selectedFixtureIds.value)
  if (newSet.has(id)) {
    newSet.delete(id)
  } else {
    newSet.add(id)
  }
  selectedFixtureIds.value = newSet
}

const isFixtureSelected = (id) => {
  return selectedFixtureIds.value.has(id)
}

const toggleDateGroup = (fixtures) => {
  const ids = fixtures.map(f => f.id)
  const allSelected = ids.every(id => selectedFixtureIds.value.has(id))
  const newSet = new Set(selectedFixtureIds.value)
  if (allSelected) {
    ids.forEach(id => newSet.delete(id))
  } else {
    ids.forEach(id => newSet.add(id))
  }
  selectedFixtureIds.value = newSet
}

const isDateGroupSelected = (fixtures) => {
  return fixtures.every(f => selectedFixtureIds.value.has(f.id))
}

const isDateGroupPartial = (fixtures) => {
  const some = fixtures.some(f => selectedFixtureIds.value.has(f.id))
  const all = fixtures.every(f => selectedFixtureIds.value.has(f.id))
  return some && !all
}

const toggleBulkMarket = (market) => {
  const idx = selectedBulkMarkets.value.indexOf(market)
  if (idx >= 0) {
    selectedBulkMarkets.value.splice(idx, 1)
  } else {
    selectedBulkMarkets.value.push(market)
  }
}

const clearSelection = () => {
  selectedFixtureIds.value = new Set()
}

const downloadBulkMilestones = async () => {
  if (selectedFixtureIds.value.size === 0) return
  isBulkDownloading.value = true
  try {
    const response = await axios.post('/api/milestones/bulk-download', {
      fixture_ids: Array.from(selectedFixtureIds.value),
      market_types: selectedBulkMarkets.value.length > 0 ? selectedBulkMarkets.value : null,
    })

    const json = JSON.stringify(response.data, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `milestones_bulk_${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  } catch (err) {
    console.error('Error downloading bulk milestones:', err)
  } finally {
    isBulkDownloading.value = false
  }
}

const downloadJsonBlob = (data, filename) => {
  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const handleBulkDownload = async () => {
  if (selectedFixtureIds.value.size === 0) return
  isBulkDownloading.value = true
  try {
    const fixtureIds = Array.from(selectedFixtureIds.value)
    const marketTypes = selectedBulkMarkets.value.length > 0 ? selectedBulkMarkets.value : null

    if (bulkDownloadType.value === 'balanced_lines' || bulkDownloadType.value === 'balanced_lines_alt') {
      const onlyBalanced = bulkDownloadType.value === 'balanced_lines'
      const response = await axios.post('/api/fixtures/bulk-balanced-lines', {
        fixture_ids: fixtureIds,
        market_types: marketTypes,
        only_balanced: onlyBalanced,
      })
      const prefix = onlyBalanced ? 'balanced_lines' : 'balanced_lines_with_alt'
      downloadJsonBlob(response.data, `${prefix}_bulk_${Date.now()}.json`)
    } else {
      await downloadBulkMilestones()
    }
  } catch (err) {
    console.error('Error downloading bulk data:', err)
  } finally {
    isBulkDownloading.value = false
  }
}

// Will be inplay toggle state
const willBeInplayLoading = ref({})

const isWillBeInplayLoading = (id) => !!willBeInplayLoading.value[id]

const toggleWillBeInplay = async (fixture, event) => {
  event?.stopPropagation?.()
  const id = fixture.id
  if (willBeInplayLoading.value[id]) return
  const desired = !Boolean(fixture.will_be_inplay)
  willBeInplayLoading.value = { ...willBeInplayLoading.value, [id]: true }
  try {
    const { data } = await axios.put(`/api/fixtures/${id}/will-be-inplay`, {
      will_be_inplay: desired,
    })
    const idx = recentFixtures.value.findIndex((f) => f.id === id)
    if (idx !== -1) {
      recentFixtures.value[idx].will_be_inplay = data.will_be_inplay
    }
  } catch (error) {
    console.error('Error updating will_be_inplay', error)
  } finally {
    const next = { ...willBeInplayLoading.value }
    delete next[id]
    willBeInplayLoading.value = next
  }
}

// Editable gamedate state
const editingGamedate = ref({})
const gamedateInput = ref({})
const savingGamedate = ref({})

const startEditingGamedate = (fixture) => {
  // Convert gamedate from 'd.m.Y H:i' to 'dd.mm.yyyy, HH:mm' for DateTimePickerCustom
  const dateStr = fixture.gamedate // Format: 'd.m.Y H:i'
  if (dateStr) {
    const [datePart, timePart] = dateStr.split(' ')
    const [day, month, year] = datePart.split('.')
    // Format: 'dd.mm.yyyy, HH:mm'
    const formattedDate = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}, ${timePart}`
    gamedateInput.value[fixture.id] = formattedDate
  } else {
    gamedateInput.value[fixture.id] = ''
  }
  editingGamedate.value[fixture.id] = true
}

const cancelEditingGamedate = (fixtureId) => {
  editingGamedate.value[fixtureId] = false
  delete gamedateInput.value[fixtureId]
}

const saveGamedate = async (fixture) => {
  if (!gamedateInput.value[fixture.id]) {
    return
  }

  savingGamedate.value[fixture.id] = true
  
  try {
    // Convert DateTimePickerCustom format 'dd.mm.yyyy, HH:mm' to 'Y-m-d H:i:s'
    const dateTimeStr = gamedateInput.value[fixture.id] // Format: 'dd.mm.yyyy, HH:mm'
    const [datePart, timePart] = dateTimeStr.split(', ')
    const [day, month, year] = datePart.split('.')
    const formattedDateTime = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} ${timePart}:00`

    const response = await axios.put(
      `/api/fixtures/${fixture.id}/gamedate`,
      { gamedate: formattedDateTime }
    )

    // Update the fixture in the list
    const index = recentFixtures.value.findIndex(f => f.id === fixture.id)
    if (index !== -1) {
      recentFixtures.value[index] = response.data.fixture
    }

    editingGamedate.value[fixture.id] = false
    delete gamedateInput.value[fixture.id]
  } catch (err) {
    console.error('Error updating gamedate:', err)
    alert('Failed to update gamedate: ' + (err.response?.data?.message || err.message))
  } finally {
    savingGamedate.value[fixture.id] = false
  }
}
</script>

<template>
  <div :class="{ 'pb-20': canBulkDownload && hasSelectedFixtures }">
   

    <!-- Create New Fixture Form -->
    <CreateFixtureForm @fixtureCreated="handleNewFixture" />


    <!-- Recent Fixtures -->
    <div class="rounded-lg">
      <div class="p-4 flex justify-between items-center">
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-3">
            <i class="fas fa-clock text-pink-500"></i> Recent Fixtures
            <!-- <button 
              @click="toggleSortOrder"
              class="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
              :title="sortOrder === 'asc' ? 'Oldest First' : 'Newest First'"
            >
              <span class="text-xs">Date</span>
              <i 
                class="fas"
                :class="{
                  'fa-sort-amount-down-alt': sortOrder === 'desc',
                  'fa-sort-amount-up-alt': sortOrder === 'asc'
                }"
              ></i>
            </button> -->
          </div>
          
          <!-- Quick select buttons -->
          <div class="flex gap-2">
            <button
              v-for="range in quickRanges"
              :key="range.value"
              @click="fixturesRange = range.value"
              class="px-3 py-1.5 text-sm rounded-full transition-colors"
              :class="fixturesRange === range.value ? 
                'bg-pink-500 text-white' : 
                'bg-gray-100 text-gray-600 hover:bg-gray-200'"
            >
              {{ range.label }}
            </button>
          </div>

          <!-- Range dropdown -->
          <div class="relative range-dropdown">
            <div 
              @click="showRangeDropdown = !showRangeDropdown"
              class="flex bg-white items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 text-sm"
            >
              <i class="fas fa-calendar text-gray-400"></i>
              <span class="text-gray-700">{{ getRangeText }}</span>
              <i class="fas fa-chevron-down text-gray-400 text-xs ml-2"></i>
            </div>

            <div 
              v-if="showRangeDropdown" 
              class="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border p-4 min-w-[900px] z-10 grid grid-cols-[300px_1fr] gap-6"
              @click.stop
            >
              <!-- Left side - Options -->
              <div class="border-r pr-4">
                <!-- Previous days section -->
                <div class="mb-6">
                  <div class="px-2 py-2 text-xs font-medium text-gray-500">PREVIOUS</div>
                  <div 
                    v-for="option in rangeOptions.filter(opt => opt.value.startsWith('prev'))"
                    :key="option.value"
                    @click="selectRange(option.value)"
                    class="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm flex items-center gap-2 rounded"
                    :class="{ 'text-pink-500 bg-pink-50': fixturesRange === option.value }"
                  >
                    <i class="fas fa-check text-xs" v-if="fixturesRange === option.value"></i>
                    <span :class="{ 'ml-4': fixturesRange !== option.value }">{{ option.label }}</span>
                  </div>
                </div>

                <!-- Next days section -->
                <div class="mb-6">
                  <div class="px-2 py-2 text-xs font-medium text-gray-500">NEXT</div>
                  <div 
                    v-for="option in rangeOptions.filter(opt => opt.value.startsWith('next'))"
                    :key="option.value"
                    @click="selectRange(option.value)"
                    class="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm flex items-center gap-2 rounded"
                    :class="{ 'text-pink-500 bg-pink-50': fixturesRange === option.value }"
                  >
                    <i class="fas fa-check text-xs" v-if="fixturesRange === option.value"></i>
                    <span :class="{ 'ml-4': fixturesRange !== option.value }">{{ option.label }}</span>
                  </div>
                </div>

                <!-- Custom range option -->
                <div>
                  <div 
                    @click="selectRange('custom')"
                    class="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm flex items-center gap-2 rounded"
                    :class="{ 'text-pink-500 bg-pink-50': fixturesRange === 'custom' }"
                  >
                    <i class="fas fa-check text-xs" v-if="fixturesRange === 'custom'"></i>
                    <span :class="{ 'ml-4': fixturesRange !== 'custom' }">Custom Range</span>
                  </div>
                </div>
              </div>

              <!-- Right side - Calendar -->
              <div>
                <DateRangePicker
                  v-model="customRange"
                  :highlighted-dates="fixtureDates"
                  @apply="applyCustomRange"
                />
                <button
                  @click="showFixtureCalendar = true; showRangeDropdown = false"
                  class="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition text-sm"
                >
                  <i class="fas fa-th"></i>
                  Full Calendar Overview
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- <div class="flex gap-3">
          <button class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-sliders-h mr-2"></i> Advanced Filters
          </button>
          <button class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-download mr-2"></i> Export
          </button>
        </div> -->
      </div>

      <div class="space-y-6">
        <template v-for="(fixtures, date) in groupedFixtures" :key="date">
          <!-- Date Group Card -->
          <div class="bg-white rounded-lg shadow overflow-hidden">
            <!-- Date Header -->
            <div class="bg-gray-700 px-6 py-3 border-b flex items-center justify-between">
              <div class="flex items-center gap-2">
                <i class="fas fa-calendar-day text-gray-100"></i>
                <span class="font-semibold text-gray-100">{{ date }}</span>
                <span class="text-sm text-gray-100">({{ fixtures.length }} games)</span>
              </div>
            </div>

            <!-- Column Headers -->
            <div class="grid grid-cols-[40px_1fr_100px_2.5fr_1fr_1fr] px-6 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div>
                <input type="checkbox" class="rounded">
              </div>
              <div>FIXTURE ID</div>
              <div>TIME</div>
              <div class="text-center">TEAMS</div>
              <div>STATUS</div>
              <div class="text-right">ACTIONS</div>
            </div>

            <!-- Fixtures List -->
            <div class="divide-y divide-gray-100">
              <div 
                v-for="fixture in fixtures" 
                :key="fixture.id" 
                class="grid grid-cols-[40px_1fr_100px_2.5fr_1fr_1fr] px-6 py-3 hover:bg-gray-50 items-center"
              >
                <div>
                  <input type="checkbox" class="rounded">
                </div>
                
                <div>
                  <div class="flex items-center gap-2">
                    <span 
                      class="text-pink-500 cursor-pointer hover:text-pink-600"
                      @click="handleFixtureClick(fixture.id)"
                    >
                      #{{ fixture.id }} asd
                    </span>

                    <a 
                      :href="`/fixture/${fixture.id}`" 
                      target="_blank"
                      class="text-gray-400 hover:text-gray-600"
                    >
                      <i class="fas fa-external-link-alt text-xs"></i>
                    </a>
                  </div>
                </div>

                <div v-if="fixture.time" class="text-sm text-gray-500">
                  {{ formatTime(fixture.time) }}
                </div>

                <div>
                  <div class="flex items-center justify-center w-full">
                    <div class="grid grid-cols-[180px_40px_180px] items-center">
                      <div class="text-right">
                        <span class="font-medium text-gray-900 hover:text-pink-600 cursor-pointer">
                          {{ fixture.home_team?.team_name || '—' }}
                        </span>
                      </div>
                      
                      <div class="flex items-center justify-center">
                        <span class="text-xs text-gray-400 font-medium">vs</span>
                      </div>
                      
                      <div class="text-left">
                        <span class="font-medium text-gray-900 hover:text-pink-600 cursor-pointer">
                          {{ fixture.away_team?.team_name || '—' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <span class="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    {{ fixture.status }}
                  </span>
                </div>

                <div class="flex justify-end gap-2">
                  <button class="text-gray-400 hover:text-gray-600">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    class="text-gray-400 hover:text-red-600"
                    @click="handleDeleteFixture(fixture)"
                    title="Delete fixture"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Recent Fixtures Section -->
    <div class="mb-8">
      <div v-if="isLoadingRecent" class="text-gray-400 py-4">Loading recent fixtures...</div>
      <div v-else-if="recentError" class="text-red-500 py-4">{{ recentError }}</div>
      <div v-else-if="recentFixtures.length === 0" class="text-gray-400 py-4">No recent fixtures found.</div>
      <div v-else class="space-y-6">
        <template v-for="(fixtures, date) in groupedRecentFixtures" :key="`recent-${date}`">
          <!-- Date Group Card -->
          <div class="bg-white rounded-lg shadow overflow-hidden">
            <!-- Date Header -->
            <div class="bg-gray-700 px-6 py-3 border-b flex items-center justify-between">
              <div class="flex items-center gap-2">
                <input
                  v-if="canBulkDownload"
                  type="checkbox"
                  class="rounded border-gray-400 cursor-pointer"
                  :checked="isDateGroupSelected(fixtures)"
                  :indeterminate="isDateGroupPartial(fixtures)"
                  @change="toggleDateGroup(fixtures)"
                />
                <i class="fas fa-calendar-day text-white"></i>
                <span class="font-semibold text-white">{{ date }}</span>
                <span class="text-sm text-gray-100">({{ fixtures.length }} games)</span>
              </div>
            </div>

            <!-- Column Headers -->
            <div
              :class="hasAnyPermission(['fixtureEdit']) ? 'grid-cols-[40px_1fr_160px_2.5fr_1fr_80px_1fr_1fr_1fr]' : 'grid-cols-[40px_1fr_160px_2.5fr_1fr_80px_1fr]'"
              class="grid px-6 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              <div></div>
              <div>FIXTURE ID</div>
              <div>TIME</div>
              <div class="text-center">TEAMS</div>
              <div class="text-right">Updated AT</div>
              <div class="text-center">INPLAY</div>
              <div v-if="hasAnyPermission(['fixtureEdit'])" class="text-right">Created By</div>
              <div v-if="hasAnyPermission(['fixtureEdit'])" class="text-right">PUBLISHED AT</div>
              <div v-if="hasAnyPermission(['fixtureEdit'])" class="text-right">ACTIONS</div>
            </div>

            <!-- Fixtures List -->
            <div class="divide-y divide-gray-100">
              <div 
                v-for="fixture in fixtures" 
                :key="fixture.id" 
                :class="hasAnyPermission(['fixtureEdit']) ? 'grid-cols-[40px_1fr_160px_2.5fr_1fr_80px_1fr_1fr_1fr]' : 'grid-cols-[40px_1fr_160px_2.5fr_1fr_80px_1fr]'"
                class="grid px-6 py-3 hover:bg-gray-50 items-center"
                style="overflow: visible;"
              >
                <div>
                  <input
                    v-if="canBulkDownload"
                    type="checkbox"
                    class="rounded cursor-pointer"
                    :checked="isFixtureSelected(fixture.id)"
                    @change="toggleFixtureSelection(fixture.id)"
                  />
                </div>

                <div>

                  <div class="flex items-center gap-4">

                    <a
                      :href="userStore.currentUser.role === 'admin' ? `/fixture/${fixture.id}/projections` : `/fixture/${fixture.id}`"
                      class="text-pink-500 hover:text-pink-600 cursor-pointer"
                    >
                      #{{ fixture.id }}
                    </a>
                    <router-link
                      v-if="userStore.currentUser.role === 'admin' || userStore.currentUser.role === 'trader' || userStore.currentUser.role === 'superadmin'"
                      :to="`/fixture-v1/${fixture.id}/projections`"
                      class="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] rounded font-medium hover:bg-amber-200"
                    >
                      V1
                    </router-link>
                    <a 
                      :href="userStore.currentUser.role === 'admin' ? `/fixture/${fixture.id}/projections` : `/fixture/${fixture.id}`" 
                      target="_blank"
                      class="text-gray-400 hover:text-gray-600 flex items-center gap-1"
                    >
                      <span class="text-[12px] text-gray-400">New Tab</span>
                      <i class="fas fa-external-link-alt text-[8px]"></i>
                    </a>
                  </div>
                </div>

                <div class="text-[16px] text-gray-500 whitespace-nowrap">
                  <div v-if="!editingGamedate[fixture.id]" class="flex items-center gap-2">
                    <span class="whitespace-nowrap">{{ fixture.gamedate }}</span>
                    <button
                      @click="startEditingGamedate(fixture)"
                      class="text-gray-400 hover:text-pink-500 text-md flex-shrink-0"
                      title="Edit date"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                  </div>
                  <div v-else class="flex items-center gap-2 relative z-50">
                    <div class="w-[280px] min-w-[280px] relative" style="overflow: visible;">
                      <DateTimePickerCustom
                        v-model="gamedateInput[fixture.id]"
                        placeholder="dd.mm.yyyy --:--"
                      />
                    </div>
                    <button
                      @click="saveGamedate(fixture)"
                      class="text-green-500 hover:text-green-600 text-xs"
                      :disabled="savingGamedate[fixture.id]"
                      title="Save"
                    >
                      <i class="fas fa-check" v-if="!savingGamedate[fixture.id]"></i>
                      <i class="fas fa-spinner fa-spin" v-else></i>
                    </button>
                    <button
                      @click="cancelEditingGamedate(fixture.id)"
                      class="text-gray-400 hover:text-gray-600 text-xs"
                      :disabled="savingGamedate[fixture.id]"
                      title="Cancel"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>

                <div>
                  <div class="flex items-center justify-center w-full">
                    <div class="grid grid-cols-[180px_40px_180px] items-center">
                      <div class="text-right">
                        <span class="font-medium text-gray-900 hover:text-pink-600 cursor-pointer">
                          {{ fixture.home_team?.team_name || '—' }}
                        </span>
                      </div>
                      
                      <div class="flex items-center justify-center">
                        <span class="text-xs text-gray-400 font-medium">vs</span>
                      </div>
                      
                      <div class="text-left">
                        <span class="font-medium text-gray-900 hover:text-pink-600 cursor-pointer">
                          {{ fixture.away_team?.team_name || '—' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="text-right text-sm text-gray-500">
                  {{ fixture.updated_at }}
                </div>

                <div v-if="hasAnyPermission(['fixtureEdit'])" class="flex justify-center" @click.stop>
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="Boolean(fixture.will_be_inplay)"
                    :disabled="isWillBeInplayLoading(fixture.id)"
                    class="relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    :class="fixture.will_be_inplay ? 'bg-pink-500' : 'bg-gray-200'"
                    @click="toggleWillBeInplay(fixture, $event)"
                  >
                    <span
                      class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      :class="fixture.will_be_inplay ? 'translate-x-6' : 'translate-x-1'"
                    />
                    <span
                      v-if="isWillBeInplayLoading(fixture.id)"
                      class="absolute inset-0 flex items-center justify-center rounded-full bg-white/40"
                    >
                      <i class="fas fa-spinner fa-spin text-pink-600 text-xs" />
                    </span>
                  </button>
                </div>
                <div v-else class="flex justify-center">
                  <span
                    v-if="fixture.will_be_inplay"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-pink-100 text-pink-700"
                  >
                    Yes
                  </span>
                </div>

                <div v-if="hasAnyPermission(['fixtureEdit'])" class="text-right text-sm text-gray-500">
                  {{ fixture.user?.name}}
                </div>

                <div v-if="hasAnyPermission(['fixtureEdit'])" class="text-right text-sm text-gray-500">
                  <span
                    v-if="fixture.published_at"
                    class="text-right text-sm px-2 py-1 rounded bg-green-100 text-green-700 font-medium"
                  >
                    {{ fixture.published_at }}
                  </span>
                  <span
                    v-else
                    class="text-right text-sm text-gray-500"
                  >
                    {{ fixture.published_at }}
                  </span>
                </div>

                <div
                  v-if="hasAnyPermission(['fixtureEdit'])"
                  class="flex justify-end gap-2"
                >
                  <!-- <button class="text-gray-400 hover:text-gray-600">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="text-gray-400 hover:text-gray-600">
                    <i class="fas fa-edit"></i>
                  </button> -->
                  <button 
                    class="text-gray-400 hover:text-red-600"
                    @click="handleDeleteFixture(fixture)"
                    title="Delete fixture"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Pagination -->
    <div class="p-4 border-t flex items-center justify-between text-sm text-gray-500">
      <!-- <div>Showing 1 to 10 of 70 entries</div>
      <div class="flex gap-2">
        <button class="px-3 py-1 border rounded hover:bg-gray-50">Previous</button>
        <button class="px-3 py-1 bg-pink-500 text-white rounded">1</button>
        <button class="px-3 py-1 border rounded hover:bg-gray-50">2</button>
        <button class="px-3 py-1 border rounded hover:bg-gray-50">3</button>
        <button class="px-3 py-1 border rounded hover:bg-gray-50">...</button>
        <button class="px-3 py-1 border rounded hover:bg-gray-50">7</button>
        <button class="px-3 py-1 border rounded hover:bg-gray-50">Next</button>
      </div> -->
    </div>

    <!-- Sticky Bulk Download Bar -->
    <div
      v-if="canBulkDownload && hasSelectedFixtures"
      class="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-pink-500 shadow-lg z-50 px-6 py-3"
    >
      <div class="max-w-screen-xl mx-auto flex items-center gap-4 flex-wrap">
        <!-- Selection count + clear -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <span class="text-sm font-medium text-gray-700">
            {{ selectedFixtureIds.size }} fixture{{ selectedFixtureIds.size !== 1 ? 's' : '' }} selected
          </span>
          <button
            @click="clearSelection"
            class="text-xs text-gray-400 hover:text-gray-600 underline"
          >
            Clear
          </button>
        </div>

        <!-- Divider -->
        <div class="w-px h-6 bg-gray-300 flex-shrink-0"></div>

        <!-- Download type toggle -->
        <div class="flex items-center gap-1 flex-shrink-0 bg-gray-100 rounded-lg p-0.5">
          <button
            @click="bulkDownloadType = 'balanced_lines'"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
            :class="bulkDownloadType === 'balanced_lines' ? 'bg-pink-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'"
          >
            Balanced Lines
          </button>
          <button
            @click="bulkDownloadType = 'balanced_lines_alt'"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
            :class="bulkDownloadType === 'balanced_lines_alt' ? 'bg-pink-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'"
          >
            BL + Alt Lines
          </button>
          <button
            @click="bulkDownloadType = 'milestones'"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
            :class="bulkDownloadType === 'milestones' ? 'bg-pink-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'"
          >
            Milestones
          </button>
        </div>

        <!-- Divider -->
        <div class="w-px h-6 bg-gray-300 flex-shrink-0"></div>

        <!-- Market checkboxes (scrollable) -->
        <div class="flex items-center gap-3 overflow-x-auto flex-1 min-w-0">
          <span class="text-xs text-gray-500 flex-shrink-0 uppercase font-medium">Markets:</span>
          <label
            v-for="market in availableBulkMarkets"
            :key="market.value"
            class="flex items-center gap-1 flex-shrink-0 cursor-pointer"
          >
            <input
              type="checkbox"
              class="rounded text-pink-500 cursor-pointer"
              :checked="selectedBulkMarkets.includes(market.value)"
              @change="toggleBulkMarket(market.value)"
            />
            <span class="text-xs text-gray-600 whitespace-nowrap">{{ market.label }}</span>
          </label>
        </div>

        <!-- Download button -->
        <button
          @click="handleBulkDownload"
          :disabled="isBulkDownloading || selectedBulkMarkets.length === 0"
          class="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 text-sm font-medium"
        >
          <i v-if="isBulkDownloading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-download"></i>
          Download JSON
        </button>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <ConfirmationDialog
      :show="showDeleteConfirmation"
      title="Delete Fixture"
      :message="`Are you sure you want to delete fixture #${fixtureToDelete?.id}? This action cannot be undone.`"
      confirm-text="Delete Fixture"
      cancel-text="Cancel"
      confirm-button-class="bg-red-600 hover:bg-red-700"
      :required-id="fixtureToDelete?.id || ''"
      @confirm="confirmDeleteFixture"
      @cancel="cancelDeleteFixture"
      @close="cancelDeleteFixture"
    />

    <!-- Fixture Calendar Modal -->
    <FixtureCalendarModal
      :visible="showFixtureCalendar"
      :league-id="props.filters.league_id"
      :league-name="currentLeagueName"
      @close="showFixtureCalendar = false"
      @select-fixture="(id) => { showFixtureCalendar = false; handleFixtureClick(id) }"
    />
  </div>
</template>

<style scoped>
.range-dropdown {
  @apply relative;
}

.range-dropdown > div:last-child {
  transform-origin: top left;
}

.fa-sort-amount-up-alt,
.fa-sort-amount-down-alt {
  @apply text-gray-400;
}

button:hover .fa-sort-amount-up-alt,
button:hover .fa-sort-amount-down-alt {
  @apply text-gray-600;
}
</style> 